// Undo/Redo manager — stores operation snapshots in localStorage

import { TasksAPI } from './api.ts';
import type { TaskWithTags, CreateTaskInput } from './types.ts';

export type UndoAction =
  | { type: 'create'; taskId: string }
  | { type: 'update'; taskId: string; oldValues: Record<string, unknown> }
  | { type: 'delete'; task: TaskWithTags }
  | { type: 'move'; taskId: string; oldBeforeId: string | null; oldParentId: string | null }
  | { type: 'nest'; taskId: string; oldParentId: string | null; oldPosition: number };

const STORAGE_KEY = 'tmk_undo_stack';
const REDO_KEY = 'tmk_redo_stack';
const MAX_STACK = 50;

function loadStack(key: string): UndoAction[] {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
}

function saveStack(key: string, stack: UndoAction[]) {
  localStorage.setItem(key, JSON.stringify(stack.slice(-MAX_STACK)));
}

export const UndoManager = {
  push(action: UndoAction) {
    const stack = loadStack(STORAGE_KEY);
    stack.push(action);
    saveStack(STORAGE_KEY, stack);
    // Clear redo stack on new action
    saveStack(REDO_KEY, []);
    UndoManager.onChanged?.();
  },

  canUndo(): boolean {
    return loadStack(STORAGE_KEY).length > 0;
  },

  canRedo(): boolean {
    return loadStack(REDO_KEY).length > 0;
  },

  async undo(): Promise<boolean> {
    const stack = loadStack(STORAGE_KEY);
    const action = stack.pop();
    if (!action) return false;

    saveStack(STORAGE_KEY, stack);

    try {
      const reverseAction = await applyUndo(action);
      if (reverseAction) {
        const redoStack = loadStack(REDO_KEY);
        redoStack.push(reverseAction);
        saveStack(REDO_KEY, redoStack);
      }
      UndoManager.onChanged?.();
      return true;
    } catch (err) {
      console.error('Undo failed:', err);
      UndoManager.onChanged?.();
      return false;
    }
  },

  async redo(): Promise<boolean> {
    const redoStack = loadStack(REDO_KEY);
    const action = redoStack.pop();
    if (!action) return false;

    saveStack(REDO_KEY, redoStack);

    try {
      const reverseAction = await applyUndo(action);
      if (reverseAction) {
        const stack = loadStack(STORAGE_KEY);
        stack.push(reverseAction);
        saveStack(STORAGE_KEY, stack);
      }
      UndoManager.onChanged?.();
      return true;
    } catch (err) {
      console.error('Redo failed:', err);
      UndoManager.onChanged?.();
      return false;
    }
  },

  // Callback for UI to update button state
  onChanged: null as (() => void) | null,
};

async function applyUndo(action: UndoAction): Promise<UndoAction | null> {
  switch (action.type) {
    case 'create': {
      // Undo create = delete the task. Save it for redo.
      const task = await TasksAPI.getById(action.taskId);
      await TasksAPI.delete(action.taskId);
      return task ? { type: 'delete', task: task as TaskWithTags } : null;
    }

    case 'update': {
      // Undo update = restore old values. Save current values for redo.
      const current = await TasksAPI.getById(action.taskId);
      if (!current) return null;
      const currentValues: Record<string, unknown> = {};
      for (const key of Object.keys(action.oldValues)) {
        currentValues[key] = (current as Record<string, unknown>)[key];
      }
      await TasksAPI.update(action.taskId, action.oldValues);
      return { type: 'update', taskId: action.taskId, oldValues: currentValues };
    }

    case 'delete': {
      // Undo delete = recreate the task
      const input: CreateTaskInput = {
        title: action.task.title,
        description: action.task.description,
        deadline: action.task.deadline,
        execution_time: action.task.execution_time,
        estimated_minutes: action.task.estimated_minutes,
        planned_start_date: action.task.planned_start_date,
        planned_start_time: action.task.planned_start_time,
        parent_id: action.task.parent_id,
        position: action.task.position,   // wróć na pierwotne miejsce, nie na koniec
        priority: action.task.priority,
        hidden: action.task.hidden,
        assigned_email: action.task.assigned_email,
        tag_ids: (action.task.tags || []).map(t => ({ tag_id: t.tag_id })),
      };
      const created = await TasksAPI.create(input);
      return { type: 'create', taskId: created.id };
    }

    case 'move': {
      // Undo move = move back
      const current = await TasksAPI.getById(action.taskId);
      if (!current) return null;
      // Find what beforeId was (sibling after current position)
      const currentBeforeId: string | null = null; // simplified — move to end of old parent
      const currentParentId = current.parent_id;
      await TasksAPI.move(action.taskId, action.oldBeforeId, action.oldParentId);
      return { type: 'move', taskId: action.taskId, oldBeforeId: currentBeforeId, oldParentId: currentParentId };
    }

    case 'nest': {
      // Undo nest = move back to old parent at old position
      const current = await TasksAPI.getById(action.taskId);
      if (!current) return null;
      const currentParentId = current.parent_id;
      const currentPosition = current.position;
      // Use move to put it back
      await TasksAPI.move(action.taskId, null, action.oldParentId);
      return { type: 'nest', taskId: action.taskId, oldParentId: currentParentId, oldPosition: currentPosition };
    }
  }
}
